import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  studentMessagingApi,
  type StudentSearchResult,
} from "../../api/student-messaging.api";
import { studentErrorMessage } from "./student-shared";

/** Recherche d'un élève par son nom : à partir de 2 caractères, après un court délai de frappe. */
export function StudentSearch({
  scope,
  label,
  exclude = [],
  busy = false,
  onPick,
}: {
  scope: string;
  label: string;
  /** Élèves déjà retenus, retirés des résultats. */
  exclude?: string[];
  busy?: boolean;
  onPick: (student: StudentSearchResult) => void;
}) {
  const [term, setTerm] = useState("");
  const [search, setSearch] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const typed = term.trim();
  useEffect(() => {
    const timer = window.setTimeout(
      () => setSearch(typed.length >= 2 ? typed : ""),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [typed]);
  const results = useQuery({
    queryKey: ["student-messaging-students", scope, search],
    queryFn: ({ signal }) => studentMessagingApi.students(search, signal),
    enabled: search.length >= 2,
    retry: false,
  });
  const found = (Array.isArray(results.data) ? results.data : []).filter(
    (student) => !exclude.includes(student.id),
  );

  let status: ReactNode = null;
  if (typed.length < 2) status = "Saisissez au moins 2 caractères du nom.";
  else if (typed !== search || results.isPending) status = "Recherche…";
  else if (results.isError)
    status = studentErrorMessage(
      results.error,
      "Recherche indisponible. Réessayez.",
    );
  else if (found.length === 0) status = "Aucun élève trouvé.";

  return (
    <div className="messaging-student-search">
      <label>
        {label}
        <input
          ref={input}
          type="search"
          placeholder="Nom de l’élève"
          autoComplete="off"
          maxLength={120}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          // Entrée ne valide pas le formulaire englobant : on choisit un élève dans les résultats.
          onKeyDown={(event) => event.key === "Enter" && event.preventDefault()}
        />
      </label>
      {status ? (
        <p role="status" className="messaging-hint">
          {status}
        </p>
      ) : (
        <ul className="messaging-student-results" aria-label="Élèves trouvés">
          {found.map((student) => (
            <li key={student.id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  onPick(student);
                  setTerm("");
                  setSearch("");
                  input.current?.focus();
                }}
              >
                {student.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
